import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import SearchBar from '../components/SearchBar';

const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];
const STATUSES = ['Pending', 'In Progress', 'Completed', 'Cancelled'];

const PRIORITY_COLORS = {
  Low: 'bg-gray-100 text-gray-700',
  Medium: 'bg-blue-100 text-blue-700',
  High: 'bg-orange-100 text-orange-700',
  Urgent: 'bg-red-100 text-red-700',
};
const STATUS_ICONS = {
  Pending: <Clock size={13} className="text-yellow-600" />,
  'In Progress': <AlertCircle size={13} className="text-blue-600" />,
  Completed: <CheckCircle2 size={13} className="text-green-600" />,
  Cancelled: <Clock size={13} className="text-gray-400" />,
};

export default function TasksPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [form, setForm] = useState({ title: '', description: '', assigned_to_id: '', assigned_to_name: '', due_date: '', priority: 'Medium', status: 'Pending', notes: '' });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (assigneeFilter) params.set('assigned_to', assigneeFilter);
      if (searchText) params.set('search', searchText);
      const [taskRes, userRes] = await Promise.all([
        api.get(`/tasks?${params}`),
        api.get('/users').catch(() => ({ data: [] }))
      ]);
      setTasks(taskRes.data);
      setUsers(Array.isArray(userRes.data) ? userRes.data : []);
    } catch { toast.error('Failed to load tasks'); }
    finally { setLoading(false); }
  }, [statusFilter, assigneeFilter, searchText]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => {
    setEditId(null);
    setForm({ title: '', description: '', assigned_to_id: '', assigned_to_name: '', due_date: '', priority: 'Medium', status: 'Pending', notes: '' });
    setDialogOpen(true);
  };

  const openEdit = (task) => {
    setEditId(task._id);
    setForm({
      title: task.title, description: task.description || '',
      assigned_to_id: task.assigned_to_id || '', assigned_to_name: task.assigned_to_name || '',
      due_date: task.due_date || '', priority: task.priority || 'Medium',
      status: task.status || 'Pending', notes: task.notes || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.title) { toast.error('Task title required'); return; }
    try {
      if (editId) { await api.put(`/tasks/${editId}`, form); toast.success('Task updated'); }
      else { await api.post('/tasks', form); toast.success('Task created'); }
      setDialogOpen(false); fetchData();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    try { await api.delete(`/tasks/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  const quickStatus = async (task, newStatus) => {
    try {
      await api.put(`/tasks/${task._id}`, { ...task, _id: undefined, status: newStatus });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleAssign = (uid) => {
    const u = users.find(usr => usr._id === uid);
    setForm(prev => ({ ...prev, assigned_to_id: uid, assigned_to_name: u?.name || '' }));
  };

  const pendingCount = tasks.filter(t => t.status === 'Pending').length;
  const inProgressCount = tasks.filter(t => t.status === 'In Progress').length;
  const completedCount = tasks.filter(t => t.status === 'Completed').length;

  return (
    <div data-testid="tasks-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-4xl font-semibold text-[#111B11] tracking-tight">Tasks</h1>
          <p className="text-sm text-[#4F734F] mt-1">Assign and track team tasks</p>
        </div>
        <Button onClick={openNew} className="btn-accent gap-2" data-testid="add-task-btn"><Plus size={16} /> New Task</Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="stat-card"><p className="text-xs uppercase font-semibold text-[#4F734F] mb-1">Pending</p><p className="font-heading text-2xl font-light text-yellow-600">{pendingCount}</p></div>
        <div className="stat-card"><p className="text-xs uppercase font-semibold text-[#4F734F] mb-1">In Progress</p><p className="font-heading text-2xl font-light text-blue-600">{inProgressCount}</p></div>
        <div className="stat-card"><p className="text-xs uppercase font-semibold text-[#4F734F] mb-1">Completed</p><p className="font-heading text-2xl font-light text-green-600">{completedCount}</p></div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <SearchBar
          value={searchText}
          onChange={setSearchText}
          placeholder="Search task, assignee..."
          testId="task-search"
          minWidth="min-w-[280px]"
          className="!flex-none w-full sm:w-80"
        />
        <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40 h-11 input-field"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        {users.length > 0 && (
          <Select value={assigneeFilter || 'all'} onValueChange={(v) => setAssigneeFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-44 input-field"><SelectValue placeholder="All Members" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Members</SelectItem>{users.map(u => <SelectItem key={u._id} value={u._id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      {/* Task List */}
      <div className="space-y-3">
        {loading ? (
          <div className="text-center py-12 text-olive-400">Loading...</div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-12 text-olive-400 bg-white border border-[#E6EDE6] rounded-sm">No tasks found</div>
        ) : tasks.map(task => (
          <div key={task._id} className={`bg-white border border-[#E6EDE6] rounded-sm p-4 hover:shadow-sm transition-shadow ${task.status === 'Completed' ? 'opacity-60' : ''}`} data-testid={`task-${task._id}`}>
            <div className="flex items-start gap-4">
              {/* Status toggle */}
              <button
                onClick={() => quickStatus(task, task.status === 'Completed' ? 'Pending' : 'Completed')}
                className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${task.status === 'Completed' ? 'border-green-500 bg-green-500' : 'border-olive-300 hover:border-[#D4AF37]'}`}
                data-testid={`toggle-task-${task._id}`}
              >
                {task.status === 'Completed' && <CheckCircle2 size={12} className="text-white" />}
              </button>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className={`text-sm font-medium ${task.status === 'Completed' ? 'line-through text-olive-400' : 'text-[#111B11]'}`}>{task.title}</h3>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${PRIORITY_COLORS[task.priority] || 'bg-gray-100'}`}>{task.priority}</span>
                </div>
                {task.description && <p className="text-xs text-olive-500 mb-2 line-clamp-2">{task.description}</p>}
                <div className="flex items-center gap-4 text-xs text-olive-500">
                  {task.assigned_to_name && <span>Assigned to: <strong className="text-[#213021]">{task.assigned_to_name}</strong></span>}
                  {task.assigned_by_name && <span>By: {task.assigned_by_name}</span>}
                  {task.due_date && <span>Due: {task.due_date}</span>}
                  <span className="flex items-center gap-1">{STATUS_ICONS[task.status]} {task.status}</span>
                </div>
              </div>

              <div className="flex gap-1 flex-shrink-0">
                <button onClick={() => openEdit(task)} className="p-1.5 hover:bg-olive-50 rounded-sm"><Pencil size={13} className="text-olive-500" /></button>
                <button onClick={() => handleDelete(task._id)} className="p-1.5 hover:bg-red-50 rounded-sm"><Trash2 size={13} className="text-red-400" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editId ? 'Edit' : 'New'} Task</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">Task Title</Label>
              <Input value={form.title} onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))} className="input-field" placeholder="What needs to be done?" data-testid="task-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))} className="input-field" placeholder="Details..." data-testid="task-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Assign To</Label>
                <Select value={form.assigned_to_id || 'none'} onValueChange={handleAssign}>
                  <SelectTrigger className="input-field" data-testid="task-assign-select"><SelectValue placeholder="Select member" /></SelectTrigger>
                  <SelectContent><SelectItem value="none" disabled>Select</SelectItem>{users.map(u => <SelectItem key={u._id} value={u._id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Due Date</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm(prev => ({ ...prev, due_date: e.target.value }))} className="input-field" data-testid="task-due-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm(prev => ({ ...prev, priority: v }))}>
                  <SelectTrigger className="input-field"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-semibold text-[#4F734F]">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger className="input-field"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))} className="input-field" data-testid="task-notes-input" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={handleSubmit} className="btn-primary" data-testid="save-task-btn">{editId ? 'Update' : 'Create'} Task</Button>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
